import { Signal } from 'signal-polyfill';

import { adminApi } from '../../../api/admin.api';
import type {
  DocumentationUrlDto,
  OrganizationDetailDto,
  OrganizationSportCodeCreateRequestDto,
  OrganizationSportCodeDetailDto,
  OrganizationSportCodeListItemDto,
  OrganizationSportCodeUpdateRequestDto,
} from '../../../api/types';
import { navigate } from '../../../router';
import { authState, canAccessAppAdminConfiguration } from '../../../state/auth.state';
import { BaseElement } from '../../base-element';
import '../../ui/dt-button';
import '../../ui/dt-card';
import '../../ui/dt-dialog';
import '../../ui/dt-input';
import '../../ui/dt-spinner';
import './admin-config-shell';

type PageState = 'loading' | 'loaded' | 'empty' | 'error' | 'forbidden';
type SportCodeFormMode = 'create' | 'edit' | null;
type SportCodeField = 'code' | 'displayName';
type DocumentationField = 'title' | 'url';
type FormErrorKey = SportCodeField | `documentationUrls.${number}.${DocumentationField}`;

interface DocumentationUrlDraft {
  title: string;
  url: string;
}

interface SportCodeDraft {
  code: string;
  displayName: string;
  documentationUrls: DocumentationUrlDraft[];
}

type DtDialogElement = HTMLElement & {
  showDialog(): void;
};

type DtInputElement = HTMLElement & {
  value: string;
  error: string | null;
  validate(): boolean;
};

type DtButtonElement = HTMLElement & {
  loading?: boolean;
  disabled?: boolean;
};

function createEmptyDraft(): SportCodeDraft {
  return {
    code: '',
    displayName: '',
    documentationUrls: [],
  };
}

function createDraftFromDetail(detail: OrganizationSportCodeDetailDto): SportCodeDraft {
  return {
    code: detail.code ?? '',
    displayName: detail.displayName ?? '',
    documentationUrls: (detail.documentationUrls ?? []).map((item) => ({
      title: item.title ?? '',
      url: item.url ?? '',
    })),
  };
}

function buildDocumentationUrls(rows: DocumentationUrlDraft[]): DocumentationUrlDto[] | null {
  const normalized = rows
    .map((row) => ({
      title: row.title.trim(),
      url: row.url.trim(),
    }))
    .filter((row) => row.title.length > 0 || row.url.length > 0);

  return normalized.length > 0 ? normalized : null;
}

function buildCreateRequest(draft: SportCodeDraft): OrganizationSportCodeCreateRequestDto {
  return {
    code: draft.code.trim(),
    displayName: draft.displayName.trim(),
    documentationUrls: buildDocumentationUrls(draft.documentationUrls),
  };
}

function buildUpdateRequest(draft: SportCodeDraft): OrganizationSportCodeUpdateRequestDto {
  return {
    displayName: draft.displayName.trim(),
    documentationUrls: buildDocumentationUrls(draft.documentationUrls),
  };
}

const validSportCodeFields = new Set<SportCodeField>(['code', 'displayName']);

function normalizeErrorField(fieldName: string): FormErrorKey | null {
  const cleaned = fieldName
    .replace(/^\$\.?/, '')
    .replace(/^request\./i, '')
    .replace(/^organizationSportCode\./i, '');
  const documentationMatch = cleaned.match(/^documentationUrls\[(\d+)\]\.(title|url)$/i);
  if (documentationMatch) {
    const index = documentationMatch[1];
    const field = documentationMatch[2].toLowerCase() as DocumentationField;
    return `documentationUrls.${index}.${field}` as FormErrorKey;
  }

  const lastSegment = cleaned.split('.').pop() ?? cleaned;
  const normalized = `${lastSegment.charAt(0).toLowerCase()}${lastSegment.slice(1)}`;
  if (validSportCodeFields.has(normalized as SportCodeField)) {
    return normalized as SportCodeField;
  }

  return null;
}

function isAbsoluteUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol.length > 0 && parsed.hostname.length > 0;
  } catch {
    return false;
  }
}

export class AdminOrganizationSportCodesPage extends BaseElement {
  private organizationId = '';
  private loadedOrganizationId: string | null = null;
  private organization = new Signal.State<OrganizationDetailDto | null>(null);
  private sportCodes = new Signal.State<OrganizationSportCodeListItemDto[]>([]);
  private loading = new Signal.State(false);
  private error = new Signal.State<string | null>(null);
  private forbidden = new Signal.State(false);
  private formMode = new Signal.State<SportCodeFormMode>(null);
  private formLoading = new Signal.State(false);
  private formSubmitting = new Signal.State(false);
  private formErrors = new Signal.State<Partial<Record<FormErrorKey, string[]>>>({});
  private formMessage = new Signal.State<string | null>(null);
  private formDetail = new Signal.State<OrganizationSportCodeDetailDto | null>(null);
  private deleteTarget = new Signal.State<{ id: string; code: string; displayName: string } | null>(null);
  private deleteSubmitting = new Signal.State(false);
  private deleteConflictMessage = new Signal.State<string | null>(null);

  private formDraft = createEmptyDraft();
  private editingSportCodeId: string | null = null;

  static get observedAttributes(): string[] {
    return ['organization-id'];
  }

  private pageState = new Signal.Computed<PageState>(() => {
    const auth = authState.get();

    if (!auth.isAuthenticated || !canAccessAppAdminConfiguration.get() || this.forbidden.get()) {
      return 'forbidden';
    }

    if (this.loading.get()) {
      return 'loading';
    }

    if (this.error.get()) {
      return 'error';
    }

    if (this.sportCodes.get().length === 0) {
      return 'empty';
    }

    return 'loaded';
  });

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name !== 'organization-id' || oldValue === newValue) {
      return;
    }

    this.organizationId = newValue ?? '';
    this.loadedOrganizationId = null;
    this.organization.set(null);
    this.sportCodes.set([]);
    this.error.set(null);
    this.forbidden.set(false);
    this.resetFormState();
    this.formMode.set(null);
    this.closeDeleteDialog();

    if (this.isConnected) {
      this.updateRender();
      void this.ensureLoaded();
    }
  }

  connectedCallback(): void {
    this.organizationId = this.getAttribute('organization-id') ?? this.organizationId;

    this.watch(this.organization, () => this.updateRender());
    this.watch(this.sportCodes, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.error, () => this.updateRender());
    this.watch(this.forbidden, () => this.updateRender());
    this.watch(this.formMode, () => this.updateRender());
    this.watch(this.formLoading, () => this.updateRender());
    this.watch(this.formSubmitting, () => this.updateRender());
    this.watch(this.formErrors, () => this.updateRender());
    this.watch(this.formMessage, () => this.updateRender());
    this.watch(this.formDetail, () => this.updateRender());
    this.watch(this.deleteTarget, () => this.updateRender());
    this.watch(this.deleteSubmitting, () => this.updateRender());
    this.watch(this.deleteConflictMessage, () => this.updateRender());
    this.watch(authState, () => {
      void this.ensureLoaded();
    });
    this.watch(canAccessAppAdminConfiguration, () => {
      void this.ensureLoaded();
    });

    this.updateRender();
    void this.ensureLoaded();
  }

  private canAccess(): boolean {
    const auth = authState.get();
    return auth.isAuthenticated && canAccessAppAdminConfiguration.get();
  }

  private async ensureLoaded(): Promise<void> {
    if (!this.canAccess() || !this.organizationId || this.loading.get()) {
      return;
    }

    if (this.loadedOrganizationId === this.organizationId) {
      return;
    }

    await this.loadOrganizationSportCodes();
  }

  private async loadOrganizationSportCodes(): Promise<void> {
    if (!this.canAccess() || !this.organizationId) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.forbidden.set(false);

    const [organizationResponse, sportCodesResponse] = await Promise.all([
      adminApi.organizations.getById(this.organizationId),
      adminApi.organizations.sportCodes.list(this.organizationId),
    ]);

    if (organizationResponse.error || sportCodesResponse.error) {
      this.organization.set(null);
      this.sportCodes.set([]);
      this.loading.set(false);

      const errorResponse = organizationResponse.error ?? sportCodesResponse.error;
      if (errorResponse?.status === 401 || errorResponse?.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.error.set(errorResponse?.title || 'Unable to load organization sport codes.');
      return;
    }

    if (!organizationResponse.data) {
      this.organization.set(null);
      this.sportCodes.set([]);
      this.loading.set(false);
      this.error.set('Unable to load organization details.');
      return;
    }

    this.organization.set(organizationResponse.data);
    this.sportCodes.set(sportCodesResponse.data ?? []);
    this.loadedOrganizationId = this.organizationId;
    this.loading.set(false);
  }

  private formatTimestamp(value?: string | null): string {
    if (!value) {
      return '—';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private resetFormState(): void {
    this.formDraft = createEmptyDraft();
    this.editingSportCodeId = null;
    this.formErrors.set({});
    this.formMessage.set(null);
    this.formLoading.set(false);
    this.formSubmitting.set(false);
    this.formDetail.set(null);
  }

  private openCreateDialog = (): void => {
    this.resetFormState();
    this.formMode.set('create');
  };

  private openEditDialog = async (sportCodeId: string): Promise<void> => {
    if (!this.organizationId) {
      return;
    }

    this.resetFormState();
    this.formMode.set('edit');
    this.formLoading.set(true);

    const response = await adminApi.organizations.sportCodes.getById(this.organizationId, sportCodeId);
    this.formLoading.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.formMessage.set(response.error.title || 'Unable to load sport code details.');
      return;
    }

    if (!response.data) {
      this.formMessage.set('Unable to load sport code details.');
      return;
    }

    this.editingSportCodeId = response.data.organizationSportCodeId;
    this.formDetail.set(response.data);
    this.formDraft = createDraftFromDetail(response.data);
    this.updateRender();
  };

  private closeFormDialog = (): void => {
    this.resetFormState();
    this.formMode.set(null);
  };

  private openDeleteDialog = (sportCode: OrganizationSportCodeListItemDto): void => {
    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(false);
    this.deleteTarget.set({
      id: sportCode.organizationSportCodeId,
      code: sportCode.code,
      displayName: sportCode.displayName,
    });
  };

  private closeDeleteDialog = (): void => {
    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(false);
    this.deleteTarget.set(null);
  };

  private handleRetry = (): void => {
    void this.loadOrganizationSportCodes();
  };

  private handleBackToSportCodes = (): void => {
    navigate('/admin/configuration/sport-codes');
  };

  private handleFieldInput = (event: Event): void => {
    const target = event.currentTarget as HTMLElement;
    const field = target.getAttribute('data-field') as SportCodeField | null;
    const detail = (event as CustomEvent<{ value: string }>).detail;
    if (!field) {
      return;
    }

    this.formDraft = {
      ...this.formDraft,
      [field]: detail?.value ?? '',
    };

    const nextErrors = { ...this.formErrors.get() };
    delete nextErrors[field];
    this.formErrors.set(nextErrors);

    if (this.formMessage.get() === 'Please correct the highlighted fields and try again.') {
      this.formMessage.set(null);
    }
  };

  private handleDocumentationInput = (event: Event): void => {
    const target = event.currentTarget as HTMLElement;
    const index = Number(target.getAttribute('data-doc-index'));
    const field = target.getAttribute('data-doc-field') as DocumentationField | null;
    const detail = (event as CustomEvent<{ value: string }>).detail;
    if (Number.isNaN(index) || !field) {
      return;
    }

    const nextDocumentationUrls = [...this.formDraft.documentationUrls];
    const currentRow = nextDocumentationUrls[index] ?? { title: '', url: '' };
    nextDocumentationUrls[index] = {
      ...currentRow,
      [field]: detail?.value ?? '',
    };

    this.formDraft = {
      ...this.formDraft,
      documentationUrls: nextDocumentationUrls,
    };

    const nextErrors = { ...this.formErrors.get() };
    delete nextErrors[`documentationUrls.${index}.${field}` as FormErrorKey];
    this.formErrors.set(nextErrors);

    if (this.formMessage.get() === 'Please correct the highlighted fields and try again.') {
      this.formMessage.set(null);
    }
  };

  private addDocumentationUrl = (): void => {
    this.formDraft = {
      ...this.formDraft,
      documentationUrls: [
        ...this.formDraft.documentationUrls,
        { title: '', url: '' },
      ],
    };
    this.formErrors.set({});
    this.updateRender();
  };

  private removeDocumentationUrl = (index: number): void => {
    this.formDraft = {
      ...this.formDraft,
      documentationUrls: this.formDraft.documentationUrls.filter((_, itemIndex) => itemIndex !== index),
    };
    this.formErrors.set({});
    this.updateRender();
  };

  private validateDocumentationUrls(): boolean {
    const nextErrors: Partial<Record<FormErrorKey, string[]>> = {};
    let isValid = true;

    this.formDraft.documentationUrls.forEach((row, index) => {
      const title = row.title.trim();
      const url = row.url.trim();

      if (title.length === 0 && url.length === 0) {
        return;
      }

      if (title.length === 0) {
        nextErrors[`documentationUrls.${index}.title`] = ['Title is required.'];
        isValid = false;
      }

      if (url.length === 0) {
        nextErrors[`documentationUrls.${index}.url`] = ['Url is required.'];
        isValid = false;
        return;
      }

      if (!isAbsoluteUrl(url)) {
        nextErrors[`documentationUrls.${index}.url`] = ['Url must be a valid absolute URL.'];
        isValid = false;
      }
    });

    this.formErrors.set(nextErrors);
    return isValid;
  }

  private validateForm(): boolean {
    const requiredFields: SportCodeField[] = ['displayName'];
    if (this.formMode.get() === 'create') {
      requiredFields.unshift('code');
    }

    const baseValid = requiredFields.every((field) => {
      const input = this.$(`[data-field="${field}"]`) as DtInputElement | null;
      return input ? input.validate() : true;
    });

    const documentationValid = this.validateDocumentationUrls();
    return baseValid && documentationValid;
  }

  private applyServerErrors(errors?: Record<string, string[]>): void {
    if (!errors) {
      this.formErrors.set({});
      return;
    }

    const normalizedErrors: Partial<Record<FormErrorKey, string[]>> = {};
    Object.entries(errors).forEach(([field, messages]) => {
      const normalizedField = normalizeErrorField(field);
      if (!normalizedField) {
        return;
      }

      normalizedErrors[normalizedField] = messages;
    });

    this.formErrors.set(normalizedErrors);
  }

  private getDocumentationError(index: number, field: DocumentationField): string | null {
    return this.formErrors.get()[`documentationUrls.${index}.${field}` as FormErrorKey]?.[0] ?? null;
  }

  private async handleSave(): Promise<void> {
    if (!this.organizationId || !this.formMode.get()) {
      return;
    }

    this.formMessage.set(null);
    this.formErrors.set({});

    if (!this.validateForm()) {
      this.formMessage.set('Please correct the highlighted fields and try again.');
      return;
    }

    this.formSubmitting.set(true);

    const response = this.formMode.get() === 'create'
      ? await adminApi.organizations.sportCodes.create(
          this.organizationId,
          buildCreateRequest(this.formDraft),
        )
      : await adminApi.organizations.sportCodes.update(
          this.organizationId,
          this.editingSportCodeId ?? '',
          buildUpdateRequest(this.formDraft),
        );

    this.formSubmitting.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.applyServerErrors(response.error.errors);
      this.formMessage.set(
        response.error.errors
          ? 'Please correct the highlighted fields and try again.'
          : response.error.title || 'Unable to save sport code.'
      );
      return;
    }

    this.closeFormDialog();
    await this.loadOrganizationSportCodes();
  }

  private async handleDelete(): Promise<void> {
    const target = this.deleteTarget.get();
    if (!target || !this.organizationId) {
      return;
    }

    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(true);

    const response = await adminApi.organizations.sportCodes.delete(this.organizationId, target.id);

    this.deleteSubmitting.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      if (response.error.status === 409 && response.error.errorCode === 'REFERENCED') {
        this.deleteConflictMessage.set(
          'This sport code is already referenced by existing trial events or form templates. Update those records before deleting the sport code.'
        );
        return;
      }

      this.deleteConflictMessage.set(response.error.title || 'Unable to delete sport code.');
      return;
    }

    this.closeDeleteDialog();
    await this.loadOrganizationSportCodes();
  }

  private renderLoading(): string {
    return `
      <div class="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-[24px] border border-neutral-200 bg-white" data-testid="loading-state">
        <dt-spinner size="large"></dt-spinner>
        <p class="text-sm text-neutral-600">Loading organization sport codes...</p>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="rounded-[24px] border border-error-200 bg-error-50 p-6" data-testid="error-state">
        <h2 class="text-lg font-semibold text-error-700">Unable to load sport codes</h2>
        <p class="mt-2 text-sm text-error-600">${this.esc(message)}</p>
        <dt-button class="mt-4" variant="outline" data-testid="retry-btn">Retry</dt-button>
      </div>
    `;
  }

  private renderEmpty(): string {
    const organization = this.organization.get();
    return `
      <div class="rounded-[24px] border border-dashed border-neutral-300 bg-white p-10 text-center" data-testid="empty-state">
        <div class="text-lg font-semibold text-neutral-900">No sport codes yet</div>
        <p class="mt-2 text-sm text-neutral-600">Create the first sport code for ${this.esc(organization?.name ?? 'this organization')} and optionally add documentation links used by AppAdmin workflows.</p>
        <dt-button class="mt-5" data-testid="create-empty-btn">Create sport code</dt-button>
      </div>
    `;
  }

  private renderContextCard(): string {
    const organization = this.organization.get();
    if (!organization) {
      return '';
    }

    return `
      <dt-card variant="outlined">
        <div slot="header">Organization Context</div>
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between" data-testid="organization-context-card">
          <div class="space-y-2">
            <div class="text-2xl font-semibold text-neutral-950">${this.esc(organization.name)}</div>
            <div class="flex flex-wrap items-center gap-2 text-sm text-neutral-600">
              <span class="rounded-full bg-primary-50 px-3 py-1 font-semibold text-primary-700">${this.esc(organization.code)}</span>
              <span>Created ${this.esc(this.formatTimestamp(organization.createdAtUtc))}</span>
              ${organization.updatedAtUtc ? `<span>Updated ${this.esc(this.formatTimestamp(organization.updatedAtUtc))}</span>` : ''}
            </div>
            <div class="text-sm text-neutral-600">
              ${organization.websiteUrl ? `<a href="${this.esc(organization.websiteUrl)}" class="text-primary-600 underline" target="_blank" rel="noreferrer">${this.esc(organization.websiteUrl)}</a>` : 'No website URL on file.'}
            </div>
          </div>
          <dt-button variant="outline" data-testid="back-to-sport-codes-btn">Choose another organization</dt-button>
        </div>
      </dt-card>
    `;
  }

  private renderTable(): string {
    return `
      <dt-card variant="outlined">
        <div slot="header">Sport Codes</div>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div class="max-w-2xl text-sm text-neutral-600">
              Manage organization-scoped sport codes and use the detail dialog to maintain optional documentation URLs for each code.
            </div>
            <dt-button data-testid="create-btn">Create sport code</dt-button>
          </div>

          <div class="overflow-hidden rounded-[24px] border border-neutral-200 bg-white" data-testid="sport-codes-table">
            <div class="grid grid-cols-1 gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 lg:grid-cols-[0.9fr_1.6fr_0.9fr_1fr] lg:gap-4">
              <div>Code</div>
              <div>Display Name</div>
              <div>Created</div>
              <div>Actions</div>
            </div>

            <div class="divide-y divide-neutral-200">
              ${this.sportCodes
                .get()
                .map(
                  (sportCode) => `
                    <div class="grid grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[0.9fr_1.6fr_0.9fr_1fr] lg:items-center" data-testid="sport-code-row" data-sport-code-id="${this.esc(sportCode.organizationSportCodeId)}">
                      <div class="text-sm font-semibold text-neutral-900">${this.esc(sportCode.code)}</div>
                      <div class="text-sm text-neutral-700">${this.esc(sportCode.displayName)}</div>
                      <div class="text-sm text-neutral-600">${this.esc(this.formatTimestamp(sportCode.createdAtUtc))}</div>
                      <div class="flex flex-wrap items-center gap-2">
                        <button class="rounded-full border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50" data-testid="edit-btn" data-sport-code-id="${this.esc(sportCode.organizationSportCodeId)}">Edit</button>
                        <button class="rounded-full border border-rose-200 px-3 py-1.5 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-50" data-testid="delete-btn" data-sport-code-id="${this.esc(sportCode.organizationSportCodeId)}">Delete</button>
                      </div>
                    </div>
                  `,
                )
                .join('')}
            </div>
          </div>
        </div>
      </dt-card>
    `;
  }

  private renderDocumentationEditor(): string {
    return `
      <div class="space-y-4 rounded-2xl border border-neutral-200 bg-neutral-50 p-4" data-testid="documentation-editor">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div class="text-sm font-semibold text-neutral-900">Documentation URLs</div>
            <div class="mt-1 text-sm text-neutral-600">Add zero or more absolute URLs that AppAdmin can maintain for this sport code.</div>
          </div>
          <dt-button variant="outline" data-testid="add-doc-url-btn">Add URL</dt-button>
        </div>

        ${
          this.formDraft.documentationUrls.length === 0
            ? '<div class="rounded-2xl border border-dashed border-neutral-300 bg-white px-4 py-5 text-sm text-neutral-500" data-testid="documentation-empty-state">No documentation URLs added yet.</div>'
            : `
              <div class="space-y-4">
                ${this.formDraft.documentationUrls
                  .map(
                    (_row, index) => `
                      <div class="grid gap-3 rounded-2xl border border-neutral-200 bg-white p-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)_auto] lg:items-start" data-testid="documentation-url-row">
                        <dt-input label="Title" data-doc-index="${index}" data-doc-field="title"></dt-input>
                        <dt-input label="URL" data-doc-index="${index}" data-doc-field="url"></dt-input>
                        <button class="rounded-full border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50" data-testid="remove-doc-url-btn" data-doc-index="${index}">Remove</button>
                      </div>
                    `,
                  )
                  .join('')}
              </div>
            `
        }
      </div>
    `;
  }

  private renderFormDialogBody(): string {
    const mode = this.formMode.get();
    const detail = this.formDetail.get();

    if (this.formLoading.get()) {
      return `
        <div class="flex min-h-[220px] flex-col items-center justify-center gap-3">
          <dt-spinner size="large"></dt-spinner>
          <p class="text-sm text-neutral-600">Loading sport code details...</p>
        </div>
      `;
    }

    if (mode === 'edit' && !detail) {
      return `
        <div class="rounded-2xl border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700">
          Unable to load sport code details. Close the dialog and try again.
        </div>
      `;
    }

    return `
      <div class="space-y-5">
        ${
          mode === 'edit'
            ? `
              <div class="rounded-2xl border border-neutral-200 bg-neutral-50 p-4" data-testid="sport-code-identity-summary">
                <div class="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">Sport Code Identity</div>
                <div class="mt-2 text-sm font-semibold text-neutral-900">${this.esc(detail?.code ?? '')}</div>
                <div class="mt-1 text-sm text-neutral-600">Code is immutable after create. Update the display name or documentation URLs from this dialog.</div>
              </div>
            `
            : '<dt-input label="Code" required data-field="code"></dt-input>'
        }
        <dt-input label="Display Name" required data-field="displayName"></dt-input>
        ${this.renderDocumentationEditor()}
      </div>
    `;
  }

  private renderFormDialog(): string {
    const mode = this.formMode.get();
    if (!mode) {
      return '';
    }

    return `
      <dt-dialog data-testid="sport-code-form-dialog">
        <div class="space-y-6">
          <div>
            <h2 class="text-2xl font-semibold text-neutral-950">${mode === 'create' ? 'Create sport code' : 'Edit sport code'}</h2>
            <p class="mt-2 text-sm text-neutral-600">Manage organization-scoped sport code identity and optional documentation links through the typed admin API surface.</p>
          </div>

          ${
            this.formMessage.get()
              ? `<div class="rounded-2xl border border-error-200 bg-error-50 p-4 text-sm text-error-700" data-testid="form-message">${this.esc(this.formMessage.get() ?? '')}</div>`
              : ''
          }

          ${this.renderFormDialogBody()}

          <div class="flex flex-wrap items-center justify-end gap-3">
            <dt-button variant="ghost" data-testid="cancel-form-btn">Cancel</dt-button>
            <dt-button data-testid="save-form-btn">${mode === 'create' ? 'Create sport code' : 'Save changes'}</dt-button>
          </div>
        </div>
      </dt-dialog>
    `;
  }

  private renderDeleteDialog(): string {
    const target = this.deleteTarget.get();
    if (!target) {
      return '';
    }

    return `
      <dt-dialog data-testid="delete-dialog">
        <div class="space-y-6">
          <div>
            <h2 class="text-2xl font-semibold text-neutral-950">Delete sport code</h2>
            <p class="mt-2 text-sm text-neutral-600">This removes <span class="font-semibold text-neutral-900">${this.esc(target.code)} · ${this.esc(target.displayName)}</span> only if no trial event or form template still references it.</p>
          </div>

          ${
            this.deleteConflictMessage.get()
              ? `<div class="rounded-2xl border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700" data-testid="delete-conflict-message">${this.esc(this.deleteConflictMessage.get() ?? '')}</div>`
              : ''
          }

          <div class="flex flex-wrap items-center justify-end gap-3">
            <dt-button variant="ghost" data-testid="cancel-delete-btn">Cancel</dt-button>
            <dt-button variant="outline" data-testid="confirm-delete-btn">Delete sport code</dt-button>
          </div>
        </div>
      </dt-dialog>
    `;
  }

  private renderPageBody(): string {
    const state = this.pageState.get();
    if (state === 'loading') {
      return this.renderLoading();
    }

    if (state === 'error') {
      return this.renderError(this.error.get() ?? 'Unable to load organization sport codes.');
    }

    if (state === 'empty') {
      return this.renderEmpty();
    }

    return this.renderTable();
  }

  private renderForbidden(): string {
    return `
      <section class="px-4 py-12" data-testid="forbidden-state">
        <div class="mx-auto max-w-xl text-center">
          <h2 class="text-xl font-semibold text-neutral-900">Access restricted</h2>
          <p class="mt-2 text-sm text-neutral-600">Only AppAdmin users can access the configuration area.</p>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    if (this.pageState.get() === 'forbidden') {
      this.render(this.renderForbidden());
      return;
    }

    const organization = this.organization.get();
    const sectionTitle = organization
      ? `${organization.code} Sport Codes`
      : 'Sport Codes';
    const sectionDescription = organization
      ? `Manage sport codes and optional documentation links for ${organization.name}.`
      : 'Manage organization-scoped sport codes and optional documentation links.';

    this.render(`
      <dt-admin-config-shell
        active-section="sport-codes"
        section-title="${this.esc(sectionTitle)}"
        section-description="${this.esc(sectionDescription)}"
        sport-codes-path="/admin/configuration/organizations/${this.esc(this.organizationId)}/sport-codes"
        sport-codes-title="Sport Codes"
        sport-codes-description="${this.esc(organization ? `${organization.code} scoped catalog entries and documentation links.` : 'Organization-scoped catalog entries and documentation links.') }"
      >
        ${this.renderContextCard()}
        ${this.renderPageBody()}
        ${this.renderFormDialog()}
        ${this.renderDeleteDialog()}
      </dt-admin-config-shell>
    `);

    this.attachEventListeners();
    this.assignData();
    this.syncDialogs();
  }

  private attachEventListeners(): void {
    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', this.handleRetry);
    }

    const backBtn = this.$('[data-testid="back-to-sport-codes-btn"]');
    if (backBtn) {
      backBtn.addEventListener('click', this.handleBackToSportCodes);
    }

    const createBtn = this.$('[data-testid="create-btn"]');
    if (createBtn) {
      createBtn.addEventListener('click', this.openCreateDialog);
    }

    const createEmptyBtn = this.$('[data-testid="create-empty-btn"]');
    if (createEmptyBtn) {
      createEmptyBtn.addEventListener('click', this.openCreateDialog);
    }

    this.$$('[data-testid="edit-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const sportCodeId = button.getAttribute('data-sport-code-id');
        if (sportCodeId) {
          void this.openEditDialog(sportCodeId);
        }
      });
    });

    this.$$('[data-testid="delete-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const sportCodeId = button.getAttribute('data-sport-code-id');
        const sportCode = this.sportCodes
          .get()
          .find((item) => item.organizationSportCodeId === sportCodeId);
        if (sportCode) {
          this.openDeleteDialog(sportCode);
        }
      });
    });

    const cancelFormBtn = this.$('[data-testid="cancel-form-btn"]');
    if (cancelFormBtn) {
      cancelFormBtn.addEventListener('click', this.closeFormDialog);
    }

    const saveFormBtn = this.$('[data-testid="save-form-btn"]');
    if (saveFormBtn) {
      saveFormBtn.addEventListener('click', () => {
        void this.handleSave();
      });
    }

    const addDocUrlBtn = this.$('[data-testid="add-doc-url-btn"]');
    if (addDocUrlBtn) {
      addDocUrlBtn.addEventListener('click', this.addDocumentationUrl);
    }

    this.$$('[data-testid="remove-doc-url-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.getAttribute('data-doc-index'));
        if (!Number.isNaN(index)) {
          this.removeDocumentationUrl(index);
        }
      });
    });

    const cancelDeleteBtn = this.$('[data-testid="cancel-delete-btn"]');
    if (cancelDeleteBtn) {
      cancelDeleteBtn.addEventListener('click', this.closeDeleteDialog);
    }

    const confirmDeleteBtn = this.$('[data-testid="confirm-delete-btn"]');
    if (confirmDeleteBtn) {
      confirmDeleteBtn.addEventListener('click', () => {
        void this.handleDelete();
      });
    }

    this.$$('dt-input[data-field]').forEach((input) => {
      input.addEventListener('input', this.handleFieldInput);
    });

    this.$$('dt-input[data-doc-index]').forEach((input) => {
      input.addEventListener('input', this.handleDocumentationInput);
    });

    const formDialog = this.$('[data-testid="sport-code-form-dialog"]');
    if (formDialog) {
      formDialog.addEventListener('close', this.closeFormDialog);
    }

    const deleteDialog = this.$('[data-testid="delete-dialog"]');
    if (deleteDialog) {
      deleteDialog.addEventListener('close', this.closeDeleteDialog);
    }
  }

  private assignData(): void {
    if (this.formMode.get()) {
      const codeInput = this.$('[data-field="code"]') as DtInputElement | null;
      if (codeInput) {
        codeInput.value = this.formDraft.code;
        codeInput.error = this.formErrors.get().code?.[0] ?? null;
      }

      const displayNameInput = this.$('[data-field="displayName"]') as DtInputElement | null;
      if (displayNameInput) {
        displayNameInput.value = this.formDraft.displayName;
        displayNameInput.error = this.formErrors.get().displayName?.[0] ?? null;
      }

      this.$$('dt-input[data-doc-index]').forEach((input) => {
        const index = Number(input.getAttribute('data-doc-index'));
        const field = input.getAttribute('data-doc-field') as DocumentationField | null;
        if (Number.isNaN(index) || !field) {
          return;
        }

        const dtInput = input as DtInputElement;
        dtInput.value = this.formDraft.documentationUrls[index]?.[field] ?? '';
        dtInput.error = this.getDocumentationError(index, field);
      });

      const saveBtn = this.$('[data-testid="save-form-btn"]') as DtButtonElement | null;
      if (saveBtn) {
        const disableForMissingEditDetail = this.formMode.get() === 'edit' && !this.formDetail.get();
        saveBtn.loading = this.formSubmitting.get();
        saveBtn.disabled = this.formLoading.get() || this.formSubmitting.get() || disableForMissingEditDetail;
      }
    }

    const confirmDeleteBtn = this.$('[data-testid="confirm-delete-btn"]') as DtButtonElement | null;
    if (confirmDeleteBtn) {
      confirmDeleteBtn.loading = this.deleteSubmitting.get();
      confirmDeleteBtn.disabled = this.deleteSubmitting.get();
    }
  }

  private syncDialogs(): void {
    const formDialog = this.$('[data-testid="sport-code-form-dialog"]') as DtDialogElement | null;
    if (formDialog) {
      formDialog.showDialog();
    }

    const deleteDialog = this.$('[data-testid="delete-dialog"]') as DtDialogElement | null;
    if (deleteDialog) {
      deleteDialog.showDialog();
    }
  }
}

customElements.define('dt-admin-organization-sport-codes-page', AdminOrganizationSportCodesPage);